import { tt } from "../i18n/i18n";

interface ValidationSuccess {
  isValid: true;
  bundleId: string;
  segments: string[];
}

interface ValidationError {
  isValid: false;
  error: string;
  details?: string[];
}

type ValidationResult = ValidationSuccess | ValidationError;

const reservedWords = new Set([
  "abstract",
  "assert",
  "boolean",
  "break",
  "byte",
  "case",
  "catch",
  "char",
  "class",
  "const",
  "continue",
  "default",
  "do",
  "double",
  "else",
  "enum",
  "extends",
  "final",
  "finally",
  "float",
  "for",
  "if",
  "implements",
  "import",
  "instanceof",
  "int",
  "interface",
  "long",
  "native",
  "new",
  "package",
  "private",
  "protected",
  "public",
  "return",
  "short",
  "static",
  "strictfp",
  "super",
  "switch",
  "synchronized",
  "this",
  "throw",
  "throws",
  "transient",
  "try",
  "void",
  "volatile",
  "while",
  "true",
  "false",
  "null"
]);

/**
 * Valide un identifiant de package Android (Bundle ID)
 * Selon les directives officielles Android :
 * - Doit avoir au moins deux segments
 * - Chaque segment doit commencer par une lettre
 * - Peut contenir des lettres, des chiffres et des underscores
 * - Les segments sont séparés par des points
 * - Suit généralement la notation de nom de domaine inversé
 *
 * @param {string} bundleId - L'identifiant de package Android à valider
 * @returns {ValidationResult} - Résultat de la validation avec statut et détails
 */
export function validateAndroidBundleId(bundleId: string): ValidationResult {
  if (!bundleId || typeof bundleId !== "string") {
    return {
      isValid: false,
      error: tt("android_bundle_empty_error")
    };
  }

  // Split into segments
  const segments = bundleId.split(".");
  const errors: string[] = [];

  // Must have at least two segments
  if (segments.length < 2) {
    return {
      isValid: false,
      error: tt("android_bundle_min_segments_error"),
      details: [tt("android_bundle_example")]
    };
  }

  // Validate each segment
  const segmentPattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;

  segments.forEach((segment, index) => {
    // Check if segment is empty
    if (segment.length === 0) {
      errors.push(
        tt("android_bundle_empty_segment_error", { index: index + 1 })
      );
      return;
    }

    // Check if segment is a reserved word
    if (reservedWords.has(segment.toLowerCase())) {
      errors.push(tt("android_bundle_reserved_word_error", { segment }));
      return;
    }

    // Check if segment follows pattern
    if (!segmentPattern.test(segment)) {
      errors.push(tt("android_bundle_invalid_segment_error", { segment }));
    }

    // Check length (recommended max is 255 characters)
    if (segment.length > 255) {
      errors.push(tt("android_bundle_segment_too_long_error", { segment }));
    }
  });

  // Check total length (recommended max is 1000 characters)
  if (bundleId.length > 1000) {
    errors.push(tt("android_bundle_total_length_error"));
  }

  // Return validation result
  if (errors.length > 0) {
    return {
      isValid: false,
      error: tt("android_bundle_invalid_format_error"),
      details: errors
    };
  }

  return {
    isValid: true,
    bundleId,
    segments
  };
}
